import { LoadingState } from '@/components/loading-state'
import { Minus, Plus, SlidersHorizontal } from '@phosphor-icons/react'
import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { localizedCategoryName } from '@/lib/category-labels'

import { discoveryApi } from '../api'
import {
  DEFAULT_DISCOVERY_FILTERS,
  MAX_DISTANCE_KM,
  MIN_DISTANCE_KM,
  normalizeFilters,
  type DiscoveryDateRange,
  type DiscoveryFilters,
  type DiscoveryModeFilter,
} from '../filters/filters'
import type { Category, DiscoveryLocation, Friend } from '../types'
import { CitySearch } from './city-search'
import { Sheet } from './sheet'

const DATE_RANGES: DiscoveryDateRange[] = [
  'any',
  'today',
  'tomorrow',
  'week',
  'month',
]
const MODES: DiscoveryModeFilter[] = ['all', 'open', 'approval', 'invite']

interface FiltersSheetProps {
  open: boolean
  onClose: () => void
  filters: DiscoveryFilters
  categories: Category[]
  onApply: (filters: DiscoveryFilters) => void
  onReset: () => void
}

export function FiltersSheet({
  open,
  onClose,
  filters,
  categories,
  onApply,
  onReset,
}: FiltersSheetProps) {
  const { t } = useTranslation()
  const id = useId()
  const [draft, setDraft] = useState<DiscoveryFilters>(filters)
  const [friends, setFriends] = useState<Friend[]>([])
  const [friendsPhase, setFriendsPhase] = useState<
    'loading' | 'ready' | 'error'
  >('loading')
  const [friendsAttempt, setFriendsAttempt] = useState(0)
  const [friendSearch, setFriendSearch] = useState('')
  const [friendSearchOpen, setFriendSearchOpen] = useState(
    filters.friendId !== null && filters.friendId !== 'any',
  )
  const [cityOpen, setCityOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const controller = new AbortController()
    discoveryApi
      .listFriends(controller.signal)
      .then((response) => {
        setFriends(response.friends.filter((friend) => friend.status === 'accepted'))
        setFriendsPhase('ready')
      })
      .catch(() => {
        if (!controller.signal.aborted) setFriendsPhase('error')
      })
    return () => controller.abort()
  }, [friendsAttempt, open])

  function retryFriends() {
    setFriendsPhase('loading')
    setFriendsAttempt((value) => value + 1)
  }

  const acceptedFriends = friends.filter((friend) => {
    const query = friendSearch.trim().toLowerCase()
    if (!query) return true
    return (
      friend.nickname.toLowerCase().includes(query) ||
      friend.publicName.toLowerCase().includes(query)
    )
  })

  function update(patch: Partial<DiscoveryFilters>) {
    setDraft((current) => ({ ...current, ...patch }))
  }

  function selectLocation(location: DiscoveryLocation) {
    update({ location })
    setCityOpen(false)
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      titleKey="discovery.filters.title"
      size="tall"
      footer={
        <div className="flex flex-col-reverse gap-2">
          <button
            type="button"
            className="btn-secondary w-full"
            onClick={() => {
              setDraft({ ...DEFAULT_DISCOVERY_FILTERS })
              onReset()
            }}
          >
            {t('discovery.filters.reset')}
          </button>
          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => onApply(normalizeFilters(draft))}
          >
            {t('discovery.filters.apply')}
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="rounded-md border border-line bg-white px-3 py-3">
          <div className="flex items-start gap-3">
            <input
              id={`${id}-show-past`}
              type="checkbox"
              className="mt-0.5 size-5 shrink-0 accent-teal"
              checked={draft.showPast}
              onChange={(event) => update({ showPast: event.target.checked })}
              aria-labelledby={`${id}-show-past-label`}
              aria-describedby={`${id}-show-past-hint`}
            />
            <span>
              <span id={`${id}-show-past-label`} className="block font-bold">
                {t('discovery.filters.showPast')}
              </span>
              <span
                id={`${id}-show-past-hint`}
                className="block text-sm text-muted"
              >
                {t('discovery.filters.showPastHint')}
              </span>
            </span>
          </div>
        </div>

        <div className="rounded-md border border-line bg-white px-3 py-3">
          <div className="flex items-start gap-3">
            <input
              id={`${id}-saved`}
              type="checkbox"
              className="mt-0.5 size-5 shrink-0 accent-teal"
              checked={draft.savedOnly}
              onChange={(event) => update({ savedOnly: event.target.checked })}
              aria-labelledby={`${id}-saved-label`}
              aria-describedby={`${id}-saved-hint`}
            />
            <span>
              <span id={`${id}-saved-label`} className="block font-bold">
                {t('discovery.filters.savedOnly')}
              </span>
              <span
                id={`${id}-saved-hint`}
                className="block text-sm text-muted"
              >
                {t('discovery.filters.savedOnlyHint')}
              </span>
            </span>
          </div>
        </div>

        <fieldset>
          <legend className="field-label">{t('discovery.filters.location')}</legend>
          <div className="rounded-md border border-line bg-white px-3 py-3">
            <p className="font-semibold">
              {draft.location
                ? draft.location.city ?? t('discovery.city.current')
                : t('discovery.city.none')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-ghost min-h-11 px-2"
                onClick={() => setCityOpen((value) => !value)}
                aria-expanded={cityOpen}
              >
                {t('discovery.city.choose')}
              </button>
              {draft.location ? (
                <button
                  type="button"
                  className="btn-ghost min-h-11 px-2 text-danger"
                  onClick={() => update({ location: null })}
                >
                  {t('discovery.city.clear')}
                </button>
              ) : null}
            </div>
            {cityOpen ? (
              <div className="mt-3 border-t border-line pt-3">
                <CitySearch onSelect={selectLocation} />
              </div>
            ) : null}
          </div>
        </fieldset>

        <fieldset>
          <legend className="field-label">{t('discovery.filters.friend')}</legend>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={`filter-chip ${draft.friendId === null ? 'is-active' : ''}`}
              aria-pressed={draft.friendId === null}
              onClick={() => {
                update({ friendId: null })
                setFriendSearchOpen(false)
              }}
            >
              {t('discovery.filters.allFriends')}
            </button>
            <button
              type="button"
              className={`filter-chip ${draft.friendId === 'any' ? 'is-active' : ''}`}
              aria-pressed={draft.friendId === 'any'}
              onClick={() => {
                update({ friendId: 'any' })
                setFriendSearchOpen(false)
              }}
            >
              {t('discovery.filters.anyFriend')}
            </button>
            <button
              type="button"
              className={`filter-chip ${friendSearchOpen ? 'is-active' : ''}`}
              aria-pressed={friendSearchOpen}
              onClick={() => setFriendSearchOpen(true)}
            >
              {t('discovery.filters.searchFriendOption')}
            </button>
          </div>

          {friendSearchOpen ? (
            <>
              <input
                type="search"
                value={friendSearch}
                onChange={(event) => setFriendSearch(event.target.value)}
                placeholder={t('discovery.filters.friendSearch')}
                aria-label={t('discovery.filters.friendSearch')}
                className="field-input mt-3"
              />
              <div className="mt-2 max-h-44 space-y-1 overflow-y-auto rounded-md border border-line bg-white p-1">
                {friendsPhase === 'loading' ? (
                  <LoadingState compact labelKey="common.loading" />
                ) : null}
                {friendsPhase === 'error' ? (
                  <div className="flex items-center gap-2 px-2 py-2 text-sm text-muted">
                    <span>{t('discovery.filters.friendsError')}</span>
                    <button
                      type="button"
                      className="btn-ghost min-h-11"
                      onClick={retryFriends}
                    >
                      {t('common.retry')}
                    </button>
                  </div>
                ) : null}
                {friendsPhase === 'ready' && acceptedFriends.length === 0 ? (
                  <p className="px-2 py-2 text-sm text-muted">
                    {t('discovery.filters.friendsEmpty')}
                  </p>
                ) : null}
                {acceptedFriends.map((friend) => (
                  <label
                    key={friend.friendshipId}
                    className="flex min-h-11 items-center gap-2 rounded px-2 text-sm hover:bg-mint"
                  >
                    <input
                      type="radio"
                      name={`${id}-friend`}
                      className="size-4 accent-teal"
                      checked={draft.friendId === friend.userId}
                      onChange={() => update({ friendId: friend.userId })}
                    />
                    <span className="font-semibold">{friend.publicName}</span>
                    <span className="text-muted">@{friend.nickname}</span>
                  </label>
                ))}
              </div>
            </>
          ) : null}
        </fieldset>

        <div>
          <label htmlFor={`${id}-category`} className="field-label">
            {t('discovery.filters.category')}
          </label>
          <select
            id={`${id}-category`}
            className="field-input"
            value={draft.categorySlug}
            onChange={(event) => update({ categorySlug: event.target.value })}
          >
            <option value="all">{t('discovery.categories.all')}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.slug}>
                {localizedCategoryName(t, category.slug, category.name)}
              </option>
            ))}
          </select>
        </div>

        <fieldset>
          <legend className="field-label">{t('discovery.filters.date')}</legend>
          <div className="flex flex-wrap gap-2">
            {DATE_RANGES.map((range) => (
              <button
                key={range}
                type="button"
                className={`filter-chip ${draft.dateRange === range ? 'is-active' : ''}`}
                aria-pressed={draft.dateRange === range}
                onClick={() => update({ dateRange: range })}
              >
                {t(`discovery.filters.dateRanges.${range}`)}
              </button>
            ))}
          </div>
        </fieldset>

        <div>
          <label htmlFor={`${id}-distance`} className="field-label">
            {t('discovery.filters.distance', { distance: draft.maxDistanceKm })}
          </label>
          <input
            id={`${id}-distance`}
            type="range"
            min={MIN_DISTANCE_KM}
            max={MAX_DISTANCE_KM}
            step={1}
            value={draft.maxDistanceKm}
            onChange={(event) =>
              update({ maxDistanceKm: Number(event.target.value) })
            }
            className="w-full accent-teal"
          />
          <p className="text-xs text-muted">
            {draft.location
              ? t('discovery.filters.distanceHint')
              : t('discovery.filters.distanceNoCenter')}
          </p>
        </div>

        <fieldset>
          <legend className="field-label">{t('discovery.filters.minSpots')}</legend>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="grid size-11 place-items-center rounded-full border border-line bg-white"
              onClick={() => update({ minSpots: Math.max(1, draft.minSpots - 1) })}
              aria-label={t('discovery.filters.minSpotsDecrease')}
            >
              <Minus size={18} weight="bold" aria-hidden="true" />
            </button>
            <output className="min-w-10 text-center text-lg font-bold" aria-live="polite">
              {draft.minSpots}
            </output>
            <button
              type="button"
              className="grid size-11 place-items-center rounded-full border border-line bg-white"
              onClick={() => update({ minSpots: Math.min(50, draft.minSpots + 1) })}
              aria-label={t('discovery.filters.minSpotsIncrease')}
            >
              <Plus size={18} weight="bold" aria-hidden="true" />
            </button>
            <span className="text-sm text-muted">
              {t('discovery.filters.minSpotsHint')}
            </span>
          </div>
        </fieldset>

        <fieldset>
          <legend className="field-label">
            {t('discovery.filters.participation')}
          </legend>
          <div className="flex flex-wrap gap-2">
            {MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                className={`filter-chip ${draft.participationMode === mode ? 'is-active' : ''}`}
                aria-pressed={draft.participationMode === mode}
                onClick={() => update({ participationMode: mode })}
              >
                {t(`discovery.filters.modes.${mode}`)}
              </button>
            ))}
          </div>
        </fieldset>

        <p className="flex items-start gap-2 rounded-md bg-mint px-3 py-2 text-sm text-teal">
          <SlidersHorizontal size={17} weight="bold" aria-hidden="true" className="mt-0.5 shrink-0" />
          {t('discovery.filters.persistHint')}
        </p>
      </div>
    </Sheet>
  )
}
